import { FC, ReactNode } from "react";
import { shallowEqual, useSelector } from "react-redux";

import ChinaPaymentLogos from "@Components/ChinaPaymentLogos";
import CustomLink from "@Components/CustomLink";
import chinaSocialMediaData from "@Components/FooterV2/components/FollowUs/chinaSocialMediaData";
import {
  CHINA_FOOTER_QR_TILES,
  footerListLeftChina,
  footerListRightChina,
} from "@Components/FooterV2/constant";
import getSiteNavSchema from "@Components/FooterV2/util";
import HelmetServer from "@Components/HelmetServer";
import Image from "@Components/Image";
import TrustPilotDynamicWidget from "@Components/TrustPilotDynamicWidget";
import config from "@Config/index";
import WechatOutline from "@Icons/WechatOutline";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import { EMAIL_1, ICP_LICENCE_NUMBER, ICP_URL } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { HK_WEIXIN_DWEB } from "helpers/getWeComUrlBasedOnSource";
import useTranslationHook from "hooks/useTranslationHook";

import ChinaAppStores from "./ChinaAppStores";
import styles from "./HomeV2Footer.module.scss";
import cn from "./HomeV3ChinaFooter.module.scss";

/**
 * The .cn homepage footer, laid out again so each thing has one place (references:
 * Calendly's and Monarch's brand column, monday.com's even link columns, Calendly's
 * separate downloads row):
 *
 *   brand        the logo, Trustpilot, and 关注我们 with the Chinese channels
 *   3 columns    关于我们 · 探索频道 · 帮助中心, links only, equal widths
 *   app panel    应用下载 with the China app stores, and the two QR codes at the far end
 *   bottom bar   © · T&C · Privacy · Sitemap · the ICP licence | the payment marks
 *
 * The previous layout hung the app stores, the coming-soon row and the channels under
 * 探索频道's two links, so one column ran twice as tall as its neighbours and the help
 * column stopped halfway. Content and copy are unchanged: the live China footer's
 * translation keys throughout.
 */
const HomeV3ChinaFooter: FC = () => {
  const { t } = useTranslationHook();
  const nonce = useSelector(({ appData }: AppState) => appData.nonce, shallowEqual);
  const year = new Date().getFullYear();

  const track = (buttonName: string) =>
    gtagEventEmitter({
      action: "bottom_button_clicked",
      category: "Footer Section",
      button_name: buttonName,
    });

  // As the live China footer: "新的" on Group Booking, List with us and Universities,
  // "立即申请" on Scholarships.
  const badgeFor = (key?: string) => (key ? t(key) : undefined);

  const renderLink = (href: string, label: ReactNode, gaKey: string, badge?: string) => (
    <li key={gaKey}>
      <CustomLink
        href={href}
        target="_blank"
        rel="noreferrer"
        isExternal
        className={styles.link}
        dataTestId={`v2-footer-${gaKey}`}
        onClick={() => track(gaKey)}
      >
        {label}
      </CustomLink>
      {badge && <span className={styles.badge}>{badge}</span>}
    </li>
  );

  const legalLinks = [
    { key: "home.footer.tnc", href: "/terms", gaKey: "Terms and Conditions" },
    { key: "home.footer.privacypolicy", href: "/privacy", gaKey: "Privacy Policy" },
    { key: "home.footer.sitemap", href: "/sitemap", gaKey: "Sitemap" },
  ];

  const siteNav = [
    ...footerListLeftChina.map((item) => ({ name: t(item.name), url: item.link })),
    ...footerListRightChina.map((item) => ({ name: t(item.name), url: item.link })),
    { name: t("home.footer.helpcenter"), url: "/help" },
    { name: t("home.footer.contact"), url: "/contact" },
    ...legalLinks.map((l) => ({ name: t(l.key), url: l.href })),
    ...chinaSocialMediaData.map((m) => ({ name: m.name, url: m.link })),
  ];
  const absolute = (href: string) =>
    href.startsWith("http") ? href : `${config.WEB_SERVICE_URL}${href}`;

  return (
    <footer className={styles.footer} data-testid="v2-footer">
      <HelmetServer>
        {siteNav.map((item) => (
          <script key={item.url} type="application/ld+json" suppressHydrationWarning nonce={nonce}>
            {getSiteNavSchema(item.name, absolute(item.url))}
          </script>
        ))}
      </HelmetServer>
      <div className={styles.inner}>
        <div className={cn.top}>
          {/* ─── brand: logo, rating, channels ─── */}
          <div className={cn.brand}>
            <Image
              src={`${config.IMAGE_STATIC_URL}/images/logo/amber-china-logo-dark.svg?auto=format&q=40`}
              alt="安泊 | amber"
              width="174"
              height="28"
              className={cn.logo}
            />
            {/* `.brand .cnTrustpilot` sizes the China widget as the global one */}
            <div className={`${styles.brand} ${styles.cnTrustpilot} ${cn.trust}`}>
              <TrustPilotDynamicWidget chinaVariant="footerChina" margin="0" />
            </div>
            <div className={cn.group}>
              <p className={cn.label}>{t("home.footer.followus")}</p>
              <ul className={styles.socials}>
                {chinaSocialMediaData.map((media) => (
                  <li key={media.id}>
                    <CustomLink
                      href={media.link}
                      target="_blank"
                      rel="noreferrer noopener"
                      isExternal
                      className={styles.social}
                      aria-label={media.name}
                      title={media.name}
                      onClick={() => track(media.name)}
                    >
                      <Image src={media.icon} alt="" width="20" height="20" />
                    </CustomLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* ─── three link columns, links only ─── */}
          <nav className={cn.columns} aria-label="Footer">
            <div className={cn.column}>
              <p className={cn.heading}>{t("home.footer.company")}</p>
              <ul className={styles.list}>
                {footerListLeftChina.map((item) =>
                  renderLink(item.link, t(item.name), item.gaKey, badgeFor(item.extraText)),
                )}
              </ul>
            </div>
            <div className={cn.column}>
              <p className={cn.heading}>{t("home.footer.discover")}</p>
              <ul className={styles.list}>
                {footerListRightChina.map((item) =>
                  renderLink(item.link, t(item.name), item.gaKey, badgeFor(item.extraText)),
                )}
              </ul>
            </div>
            <div className={`${cn.column} ${cn.help}`}>
              <p className={cn.heading}>{t("home.footer.support")}</p>
              <ul className={styles.list}>
                {renderLink("/help", t("home.footer.helpcenter"), "Help Center")}
                {renderLink("/contact", t("home.footer.contact"), "Contact")}
                {renderLink(
                  `mailto:${EMAIL_1}`,
                  <>
                    <span className={styles.cnContactIcon}>
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <rect x="3" y="5" width="18" height="14" rx="2.5" />
                        <path d="m4 7 8 6 8-6" />
                      </svg>
                    </span>
                    {EMAIL_1}
                  </>,
                  "Email",
                )}
                {renderLink(
                  HK_WEIXIN_DWEB,
                  <>
                    <span className={styles.cnContactIcon}>
                      {WechatOutline({ height: "18", width: "18", fill: "currentColor" })}
                    </span>
                    {t("WeChat")}
                  </>,
                  "WeChat",
                )}
              </ul>
            </div>
          </nav>
        </div>

        {/* ─── the app, in one panel: pick a store, or scan a code ───
            The stores lead and the codes sit at the far end, so the panel is filled
            edge to edge; on a phone, already on the device, the stores stay first. */}
        <section className={cn.app} aria-label={t("home.footer.gettheapp")}>
          <div className={cn.stores}>
            <p className={cn.appTitle}>{t("home.footer.gettheapp")}</p>
            <ChinaAppStores onStoreClick={track} />
          </div>
          <ul className={cn.qrs}>
            {CHINA_FOOTER_QR_TILES.map((tile) => (
              <li key={tile.id} className={styles.cnQrTile}>
                {/* Raw src: a resize modifier softens a QR code's modules past scanning. */}
                <span className={cn.qr}>
                  <Image
                    src={tile.src}
                    alt={t(tile.labelKey)}
                    width="88"
                    height="88"
                    dataTestId={`v2-footer-qr-${tile.id}`}
                  />
                </span>
                <span className={styles.cnQrLabel}>{t(tile.labelKey)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* ─── bottom bar ─── */}
        <div className={cn.bottom}>
          <ul className={cn.legal}>
            <li className={cn.copyright}>{`© ${year} amber. ${t(
              "home.footer.allrightsreserved",
            )}`}</li>
            {legalLinks.map((l) => (
              <li key={l.key}>
                <CustomLink
                  href={l.href}
                  target="_blank"
                  isExternal
                  className={styles.legalLink}
                  onClick={() => track(l.gaKey)}
                >
                  {t(l.key)}
                </CustomLink>
              </li>
            ))}
            <li>
              {/* Mainland licensing: legally required on .cn. */}
              <a
                className={styles.legalLink}
                href={ICP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ICP_LICENCE_NUMBER}
              </a>
            </li>
          </ul>
          <div className={cn.pay}>
            <span>{t("home.footer.paymentoptions")}</span>
            <div className={styles.cnPayments}>
              <ChinaPaymentLogos />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default wrapperHOC(HomeV3ChinaFooter, {
  componentName: "Footer-HomeV2-China",
  showForChina: true,
  showForWhiteLable: false,
});
